import { PDFArray, PDFDict, PDFDocument, PDFName, PDFRawStream, PDFRef, PDFStream } from "pdf-lib";

export interface PdfWeight {
  total: number;
  pictures: number; pictureCount: number;
  /** Bytes in pictures stored in scanner formats (JBIG2, JPEG 2000, CCITT) that are already compressed for scans. */
  scanPictures: number;
  fonts: number; fontFileCount: number;
  content: number;      // page content streams
  other: number;        // everything else: structure, metadata, form data, other streams
}

const streamBytes = (s: PDFStream): number => (s instanceof PDFRawStream ? s.contents.length : s.getContentsSize());

/** Roughly where the bytes in a PDF go, from the sizes of its stored streams. Works on the file as stored (already compressed). */
export async function pdfWeight(bytes: Uint8Array | ArrayBuffer): Promise<PdfWeight> {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const doc = await PDFDocument.load(u8, { ignoreEncryption: true, updateMetadata: false, throwOnInvalidObject: false });
  const ctx = doc.context;
  const fontFileRefs = new Set<string>();
  const pageContentRefs = new Set<string>();
  const imageRefs = new Set<string>();

  for (const [, obj] of ctx.enumerateIndirectObjects()) {
    if (obj instanceof PDFDict) {
      for (const key of ["FontFile", "FontFile2", "FontFile3"]) {
        const v = obj.get(PDFName.of(key));
        if (v instanceof PDFRef) fontFileRefs.add(v.toString());
      }
      const contents = obj.get(PDFName.of("Contents"));
      if (obj.get(PDFName.of("Type")) === PDFName.of("Page") && contents) {
        if (contents instanceof PDFRef) pageContentRefs.add(contents.toString());
        else if (contents instanceof PDFArray) for (let i = 0; i < contents.size(); i++) { const r = contents.get(i); if (r instanceof PDFRef) pageContentRefs.add(r.toString()); }
      }
    }
  }

  let scanPictures = 0;
  const isScanFormat = (d: PDFDict): boolean => {
    const f = d.lookup(PDFName.of("Filter"));
    const names = f instanceof PDFArray ? Array.from({ length: f.size() }, (_, i) => String(f.lookup(i))) : [String(f ?? "")];
    return names.some((n) => /JBIG2Decode|JPXDecode|CCITTFaxDecode/.test(n));
  };
  let pictures = 0, pictureCount = 0, fonts = 0, fontFileCount = 0, content = 0, streams = 0;
  for (const [ref, obj] of ctx.enumerateIndirectObjects()) {
    if (!(obj instanceof PDFStream)) continue;
    const size = streamBytes(obj);
    streams += size;
    const key = ref.toString();
    const isImage = obj.dict.get(PDFName.of("Subtype")) === PDFName.of("Image");
    if (isImage) { pictures += size; pictureCount++; imageRefs.add(key); if (isScanFormat(obj.dict)) scanPictures += size; }
    else if (fontFileRefs.has(key)) { fonts += size; fontFileCount++; }
    else if (pageContentRefs.has(key)) content += size;
  }
  const total = u8.length;
  return { total, pictures, pictureCount, scanPictures, fonts, fontFileCount, content, other: Math.max(0, total - pictures - fonts - content) };
}

const pct = (part: number, total: number) => (total > 0 ? Math.round((part / total) * 100) : 0);

/** One plain sentence on what will help, from the breakdown. */
export function weightAdvice(w: PdfWeight): string {
  if (w.scanPictures / w.total >= 0.4) return "This looks like a scan that is already compressed (JBIG2 / JPEG 2000 pictures). The usual settings can't shrink it without changing how it looks; converting pages to images is the way to get it smaller.";
  if (w.pictures / w.total >= 0.6) return "Most of this file is pictures, so Balanced should shrink it a lot, and Aggressive more.";
  if (w.pictures / w.total >= 0.25) return "Pictures are a good share of this file, so Balanced should help; the rest will not shrink much.";
  if (w.fonts / w.total >= 0.4) return "Mostly embedded fonts. Pictures are not the problem, so Balanced will only tidy it a little. Aggressive turns pages into pictures if you need it much smaller.";
  return "There are few or no pictures to shrink here, so Balanced will change little. Aggressive turns pages into pictures if you need it much smaller (text is then no longer selectable).";
}

export function weightRows(w: PdfWeight): { label: string; bytes: number; share: number; note?: string }[] {
  return [
    { label: "Pictures", bytes: w.pictures, share: pct(w.pictures, w.total), note: w.pictureCount ? `${w.pictureCount} picture${w.pictureCount > 1 ? "s" : ""}` : undefined },
    { label: "Embedded fonts", bytes: w.fonts, share: pct(w.fonts, w.total), note: w.fontFileCount ? `${w.fontFileCount} font file${w.fontFileCount > 1 ? "s" : ""}` : undefined },
    { label: "Page content", bytes: w.content, share: pct(w.content, w.total) },
    { label: "Everything else", bytes: w.other, share: pct(w.other, w.total) },
  ].filter((r) => r.bytes > 0);
}
